import { useEffect, useState } from 'react';
import { BRAND } from '../constants';

interface TrendLineProps {
    points: number[];
    stroke?: string;
    width?: number;
    height?: number;
}

// A compact yellow sparkline drawn as an SVG path — the "trend line" of the hero card.
export default function TrendLine({ points, stroke = BRAND.yellowLine, width = 180, height = 44 }: TrendLineProps) {
    const max = Math.max(...points, 1);
    const min = Math.min(...points, 0);
    const range = max - min || 1;
    const step = width / (points.length - 1 || 1);
    const coords = points.map((p, i) => {
        const x = i * step;
        const y = height - ((p - min) / range) * (height - 8) - 4;
        return [x, y] as const;
    });
    const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${line} L${width},${height} L0,${height} Z`;

    // Draw the line in slowly on mount / when the data changes.
    const [drawn, setDrawn] = useState(false);
    useEffect(() => {
        setDrawn(false);
        const t = setTimeout(() => setDrawn(true), 80);
        return () => clearTimeout(t);
    }, [line]);

    return (
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
            <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
            </defs>
            <path d={area} fill="url(#trendFill)" style={{ opacity: drawn ? 1 : 0, transition: 'opacity 1.4s ease 0.6s' }} />
            <path
                d={line}
                fill="none"
                stroke={stroke}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                pathLength={1}
                strokeDasharray={1}
                strokeDashoffset={drawn ? 0 : 1}
                style={{ transition: 'stroke-dashoffset 2.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
            />
        </svg>
    );
}
